import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { DialogService } from 'app/backpack/dialog-modal/dialog.service';
import { SchoolActivityInstance } from 'app/models';
import { AttendanceRecordService } from 'app/services/attendance-record-service';
import { SchoolActivityService } from 'app/services/school-activity.service';
import { combineLatest, map, Observable } from 'rxjs';
import { ButtonComponent } from '../../backpack/button/button.component';
import { DialogBodyComponent } from '../../backpack/dialog-modal/dialog-body.component';
import { DialogFooterComponent } from '../../backpack/dialog-modal/dialog-footer.component';
import { DialogHeaderComponent } from '../../backpack/dialog-modal/dialog-header.component';
import { StudentInstanceSearchComponent } from '../../teacher-activity/attendees/student-instance-search/student-instance-search.component';

@Component({
	standalone: true,
	selector: 'sp-add-activity-students-with-override',
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: ` <bp-dialog-header>Add Students to Activity</bp-dialog-header>
		<bp-dialog-body>
			<sp-student-instance-search
				[instance]="instance"
				[maxAttendance]="this.attendanceService.attendanceTakingOpen(this.instance.start_time) ? 0 : maxAttendees"
				[footerText]="
					attendanceService.attendanceTakingOpen(instance.start_time)
						? 'These students will be added and marked present since the activity has already started.'
						: 'After you add students to this session, only an admin can reschedule them.'
				"></sp-student-instance-search>
		</bp-dialog-body>
		<bp-dialog-footer>
			<bp-button (click)="dismiss()" variant="white" title="Cancel"></bp-button>

			<bp-button
				*ngIf="requiresOverride$ | async"
				[disabled]="false"
				(click)="dialogService.openDialog(confirmOverride, cdr)"
				variant="color"
				title="Save"></bp-button>
			<bp-button
				*ngIf="!(requiresOverride$ | async)"
				[disabled]="!(schoolActivityService.hasSearchResults$ | async)"
				(click)="addStudents()"
				variant="color"
				title="Save"></bp-button>
		</bp-dialog-footer>

		<ng-template #confirmOverride let-dismissOverride>
			<bp-dialog-header>Override Max Capacity?</bp-dialog-header>
			<bp-dialog-body>
				The max capacity for {{ activityName }} has been reached ({{ maxAttendees }} students). Do you want to override?
			</bp-dialog-body>
			<bp-dialog-footer>
				<bp-button (click)="dismissOverride()" variant="white" title="Cancel"></bp-button>
				<bp-button (click)="dismissOverride(); addStudents(true)" variant="danger" title="Override"></bp-button>
			</bp-dialog-footer>
		</ng-template>`,
	imports: [CommonModule, DialogHeaderComponent, DialogBodyComponent, StudentInstanceSearchComponent, DialogFooterComponent, ButtonComponent],
})
export class AddActivityStudentsWithOverrideComponent implements OnInit {
	attendanceService = inject(AttendanceRecordService);
	dialogService = inject(DialogService);
	cdr = inject(ChangeDetectorRef);
	schoolActivityService = inject(SchoolActivityService);

	@Input() activityName = '';
	@Input() maxAttendees = 0;
	@Input() instance!: SchoolActivityInstance;
	@Input() dismiss: () => void = () => {};

	requiresOverride$ = new Observable<boolean>();

	ngOnInit(): void {
		this.requiresOverride$ = combineLatest([
			this.schoolActivityService.attendees$(this.instance.id),
			this.schoolActivityService.selectedStudents$,
		]).pipe(
			map(([attendees, toAdd]) => {
				return this.maxAttendees < attendees.length + toAdd.length;
			})
		);
	}

	addStudents(override = false): void {
		const checkIn = this.attendanceService.attendanceTakingOpen(this.instance.start_time);
		this.schoolActivityService.SignUpSelectedStudents(this.instance, { override: override, checkIn });
		this.dismiss();
	}
}
